/**
 * SearchBar — global search field for the topbar.
 *
 * Behavior:
 *   - "/" keyboard shortcut focuses the field (unless already in an input)
 *   - "Esc" closes the results dropdown
 *   - Debounced (250ms) — no request per keystroke
 *   - Results grouped by type (Clients, Cases, Sessions, Documents, Messages)
 *   - Keyboard nav: ↑/↓ moves selection, Enter activates
 *   - Mouse: hover highlights, click activates
 *
 * Wired to GET /api/search/ — workspace-aware (passes owner_account_id
 * when the user is in a switched workspace).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineUser,
  HiOutlineFolder,
  HiOutlineClock,
  HiOutlineDocument,
  HiOutlineChatBubbleLeft,
} from 'react-icons/hi2'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import searchAPI from '../../services/search'

const TYPE_ICONS = {
  client:   HiOutlineUser,
  case:     HiOutlineFolder,
  session:  HiOutlineClock,
  document: HiOutlineDocument,
  message:  HiOutlineChatBubbleLeft,
}

const TYPE_LABELS = {
  client:   'Clients',
  case:     'Cases',
  session:  'Sessions',
  document: 'Documents',
  message:  'Messages',
}

const DEBOUNCE_MS = 250

export default function SearchBar({ className = '' }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()

  const [value, setValue] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const wrapperRef = useRef(null)
  const abortRef = useRef(null)

  // ─── Keyboard shortcut: '/' focuses the field globally ────────────────
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )
      if (e.key === '/' && !isTyping) {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ─── Click outside closes dropdown ────────────────────────────────────
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // ─── Debounced fetch ──────────────────────────────────────────────────
  useEffect(() => {
    const q = value.trim()
    if (!q) {
      setResults([])
      setLoading(false)
      return undefined
    }

    setLoading(true)
    const t = setTimeout(async () => {
      // Cancel any in-flight request before starting a new one
      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller

      try {
        const res = await searchAPI.search(q, {
          ownerAccountId: isWorkspaceSwitched ? ownerAccountId : null,
          signal: controller.signal,
        })
        const payload = res?.data?.data ?? res?.data ?? null
        if (res?.success && payload) {
          setResults(payload.results || [])
          setActiveIndex(0)
        } else {
          setResults([])
        }
      } catch (e) {
        if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
          setResults([])
        }
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(t)
  }, [value, isWorkspaceSwitched, ownerAccountId])

  // ─── Group results by type for sectioned display ──────────────────────
  const grouped = useMemo(() => {
    const order = ['client', 'case', 'session', 'document', 'message']
    const byType = {}
    for (const r of results) {
      if (!byType[r.type]) byType[r.type] = []
      byType[r.type].push(r)
    }
    return order
      .filter(t => byType[t]?.length)
      .map(t => ({ type: t, items: byType[t] }))
  }, [results])

  // Flat list for keyboard navigation
  const flatList = useMemo(
    () => grouped.flatMap(g => g.items),
    [grouped]
  )

  const handleSelect = useCallback((item) => {
    if (!item) return
    setOpen(false)
    setValue('')
    navigate(item.url)
  }, [navigate])

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (!open || flatList.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, flatList.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSelect(flatList[activeIndex])
    }
  }

  const clear = () => {
    setValue('')
    setResults([])
    inputRef.current?.focus()
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <HiOutlineMagnifyingGlass className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          placeholder={t('common.search') + '…'}
          onChange={(e) => { setValue(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label={t('common.search')}
          className="w-full ps-9 pe-16 h-9 text-sm rounded-lg bg-surface border border-border hover:border-border-strong focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 placeholder:text-ink-muted text-ink-primary transition-colors"
        />
        {value ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-muted transition-colors"
          >
            <HiOutlineXMark className="w-3.5 h-3.5" />
          </button>
        ) : (
          <kbd className="absolute end-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-medium text-ink-muted bg-muted border border-border rounded pointer-events-none">
            /
          </kbd>
        )}
      </div>

      {/* Results dropdown */}
      {open && value.trim() && (
        <div
          role="listbox"
          className="absolute start-0 end-0 mt-2 max-h-[28rem] overflow-y-auto bg-elevated border border-border rounded-xl shadow-token-lg z-50"
        >
          {loading && (
            <div className="px-4 py-6 text-xs text-ink-muted text-center">
              {t('common.loading')}
            </div>
          )}

          {!loading && flatList.length === 0 && (
            <div className="px-4 py-6 text-xs text-ink-muted text-center">
              No results for "{value.trim()}"
            </div>
          )}

          {!loading && grouped.map((group) => (
            <div key={group.type}>
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                {TYPE_LABELS[group.type]}
              </div>
              {group.items.map((item) => {
                const Icon = TYPE_ICONS[item.type] || HiOutlineDocument
                const flatIndex = flatList.indexOf(item)
                const active = flatIndex === activeIndex
                return (
                  <button
                    key={`${item.type}-${item.id}`}
                    type="button"
                    onMouseEnter={() => setActiveIndex(flatIndex)}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-start gap-3 px-3 py-2 text-start transition-colors ${
                      active ? 'bg-brand-soft text-ink-primary' : 'hover:bg-muted text-ink-primary'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-brand/20' : 'bg-muted'}`}>
                      <Icon className={`w-3.5 h-3.5 ${active ? 'text-brand' : 'text-ink-secondary'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      {item.subtitle && (
                        <p className="text-xs text-ink-muted truncate">{item.subtitle}</p>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
